import React, { useMemo } from 'react';
import HTMLRenderer from '../html-renderer/html-renderer.component';
import SearchPanelComponent from '../search-panel/search-panel.component';
import { Element } from '../html-renderer/html-renderer.component';
import { isPreviewMode, SearchPanelConfig } from 'src/utils/ssr-helper';

// PageESIWrapperComponentProps
interface PageESIWrapperComponentProps {
  children: React.ReactNode;
  searchPanelConfig?: SearchPanelConfig;
  localeInfo: string;
}

function setQueryParameters(
  type: 'HEADER' | 'FOOTER',
  localeInfo: string
): string {
  const brand = '?brand=vaa';
  const preview = isPreviewMode ? '&amp;preview=true' : '';
  const localeInfoParam = `&amp;locale=${localeInfo}`;

  if (type === 'HEADER') {
    return `${brand}${preview}${localeInfoParam}`;
  } else if (type === 'FOOTER') {
    return `${brand}${preview}`;
  } else {
    throw new Error('Invalid type on page-esi-wrapper.component.tsx');
  }
}

const generateESIInclude = (path: string, params: string): string =>
  `<!--esi <esi:include src="${path}${params}"></esi:include> --><esi:remove><!--# include virtual="${path}${params}" --></esi:remove>`;


const generateSearchQueryParams = (
  destination: string | null,
  bookingType: string | null
): string => {
  return [
    destination
      ? `&location=${destination.trim().replace(/ /g, '-').toLowerCase()}`
      : '',
    bookingType ? `&bookingType=${bookingType.trim().toLowerCase()}` : '',
  ]
    .filter(Boolean)
    .join('');
};

const createDataLayer = (): string =>
  `<esi:remove><!--# include virtual="/analytics-data-layer/home-page/" --></esi:remove>
  <esi:include src="/analytics-data-layer/home-page/"></esi:include>`;

const createConfig = (
  headerParams: string,
  searchPath: string,
  footerParams: string,
  searchQueryParams: string,
  displayButton: boolean
) => {
  return {
    header: generateESIInclude(`/next-header-footer/header${headerParams}`, ''),
    search: {
      esi: generateESIInclude(searchPath, searchQueryParams),
      displayButton,
    },
    routeMap: {
      esi: generateESIInclude('/route-map/', ''),
    },
    footer: generateESIInclude(`/next-header-footer/footer${footerParams}`, ''),
    datalayer: createDataLayer(),
  };
};

export const ESIData = (
  destination: string | null = null,
  bookingType: string | null = null,
  localeInfo: string
) => {
  const headerParams = setQueryParameters('HEADER', localeInfo);
  const footerParams = setQueryParameters('FOOTER', localeInfo);
  const searchQueryParams = generateSearchQueryParams(destination, bookingType);

  return {
    VAA: createConfig(
      headerParams,
      `/search-experience/`,
      footerParams,
      '',
      false
    ),
    VHOLS: createConfig(
      headerParams,
      `/server-rendered-search-panel/summary?brand=205`,
      footerParams,
      searchQueryParams,
      true
    ),
  };
};

function PageESIWrapperComponent({
  children,
  searchPanelConfig,
  localeInfo,
}: PageESIWrapperComponentProps) {
  const ESIObject = useMemo(() => {
    const esiData = ESIData(
      searchPanelConfig?.destination,
      searchPanelConfig?.bookingType,
      localeInfo,
    );
    return esiData.VAA;
  }, [
    searchPanelConfig?.bookingType,
    searchPanelConfig?.destination,
    localeInfo,
  ]);

  return (
    <>
      <HTMLRenderer id="next_header" htmlString={ESIObject.header} />
      <SearchPanelComponent
        isVisible={!searchPanelConfig?.hideSearchPanel}
        ESIString={ESIObject.search.esi}
        displayButton={ESIObject.search.displayButton}
      />
      {children}
      <HTMLRenderer
        id="next_footer"
        htmlString={ESIObject.footer}
        element={Element.DIV}
      />
      <HTMLRenderer id="next_datalayer" htmlString={ESIObject.datalayer} />
    </>
  );
}

export default PageESIWrapperComponent;
